import { mdiPause } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import { StepContent, StepLabel, StepSubtitle, TrackContainer, TrackStep } from './claim-stage-tracker.styles';

const STAGES = ['Coding', 'Validation', 'Transmission', 'Adjudication', 'Resolution'] as const;

type StageName = (typeof STAGES)[number];

const CODING_KEYWORDS = ['WAIT_FOR_PROFESSIONAL_CREATED', 'CODING'];
const VALIDATION_KEYWORDS = ['ELIGIBILITY', 'AUTHORIZATION', 'MEDICAL_RECORDS', 'SPLIT'];
const TRANSMISSION_KEYWORDS = ['TRANSMIT', 'TIMELY_FILING', 'WAITING_FOR_PAYER', 'WAITING_FOR_PAYMENT', 'SUBMIT_CLAIM'];
const ADJUDICATION_KEYWORDS = ['ADJUDICATION', 'ERA', 'PAYER_PROCESSING'];
const RESOLUTION_KEYWORDS = ['COLLECTIONS', 'PAID', 'DENIED', 'PATIENT_RESPONSIBILITY', 'VOID'];

const getStageFromToken = (token: string | undefined): StageName | undefined => {
    if (!token) {
        return undefined;
    }

    const normalized = token.toUpperCase();

    if (normalized.includes('HALTED') || normalized.includes('UNKNOWN') || normalized.includes('PAUSED')) {
        return undefined;
    }
    if (CODING_KEYWORDS.some(item => normalized.includes(item))) {
        return 'Coding';
    }
    if (VALIDATION_KEYWORDS.some(item => normalized.includes(item))) {
        return 'Validation';
    }
    if (TRANSMISSION_KEYWORDS.some(item => normalized.includes(item))) {
        return 'Transmission';
    }
    if (ADJUDICATION_KEYWORDS.some(item => normalized.includes(item))) {
        return 'Adjudication';
    }
    if (RESOLUTION_KEYWORDS.some(item => normalized.includes(item))) {
        return 'Resolution';
    }

    return undefined;
};

export const getMacroStageLabel = (stageToken: string | undefined): StageName => {
    return getStageFromToken(stageToken) || 'Coding';
};

export const getMacroStageIndex = (stageToken: string | undefined): number => {
    const stage = getMacroStageLabel(stageToken);
    const index = stage ? STAGES.indexOf(stage) : -1;
    return index === -1 ? 0 : index;
};

type StagePauseReason = 'halted' | 'workersPaused';

interface IClaimStageTrackerProps {
    currentStageIndex: number;
    isPaused?: boolean;
    pauseReason?: StagePauseReason;
}

const getPauseMessage = (pauseReason?: StagePauseReason): string => {
    if (pauseReason === 'workersPaused') {
        return 'Claim is paused';
    }
    return 'Claim is halted';
};

export const ClaimStageTracker: React.FC<IClaimStageTrackerProps> = ({
    currentStageIndex,
    isPaused = false,
    pauseReason,
}) => {
    const pauseMessage = getPauseMessage(pauseReason);

    return (
        <TrackContainer $isPaused={isPaused}>
            {STAGES.map((label, index) => {
                const isCompleted = index < currentStageIndex;
                const isCurrent = index === currentStageIndex;
                const shouldShowPausedTooltip = isPaused && isCurrent;

                return (
                    <TrackStep
                        key={label}
                        $isCompleted={isCompleted}
                        $isCurrent={isCurrent}
                        $isPaused={isPaused}
                        title={shouldShowPausedTooltip ? pauseMessage : undefined}>
                        <StepContent>
                            <StepLabel>
                                {index + 1}. {label}
                            </StepLabel>
                            {shouldShowPausedTooltip && (
                                <StepSubtitle>
                                    <Icon path={mdiPause} size={0.55} />
                                    {pauseMessage}
                                </StepSubtitle>
                            )}
                        </StepContent>
                    </TrackStep>
                );
            })}
        </TrackContainer>
    );
};
